// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';

const _t = () => (window).__ENGPORTAL_THEME || null;
const _dark = { bg: "#0B1120", surface: "#111827", surfaceHover: "#1a2332", border: "#1E293B", borderLight: "#334155", text: "#E2E8F0", textMuted: "#94A3B8", textDim: "#64748B", accent: "#3B82F6", accentGlow: "rgba(59, 130, 246, 0.15)", green: "#10B981", yellow: "#F59E0B", red: "#EF4444", purple: "#8B5CF6", orange: "#F97316", cardShadow: "none", mode: "dark" };
const theme = new Proxy(_dark, { get: (fallback, prop) => { const t = _t(); return t ? t[prop] : fallback[prop]; }});

const folderIcon = {
  sprint_reports: '📊', customer_presentations: '📎', external_test_data: '🧪',
  engineering_templates: '📑', sem_surface_images: '🖼️', rd_documents: '🔬',
};

export default function DocumentsPage() {
  const iStyle = { width: '100%', padding: '8px 12px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
  const lStyle = { color: theme.textMuted, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 };

  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState('all'); // 'all' or a folder value
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', tags: '', program: '', customer: '', folder: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const fileRef = useRef(null);

  const csrf = () => document.cookie.split('csrftoken=')[1]?.split(';')[0] || '';

  const loadDocs = async (folder, searchText) => {
    try {
      const params = [];
      if (folder && folder !== 'all') params.push('folder=' + encodeURIComponent(folder));
      if (searchText) params.push('search=' + encodeURIComponent(searchText));
      const qs = params.length ? '?' + params.join('&') : '';
      const res = await fetch('/api/documents/' + qs, { credentials: 'same-origin' });
      if (res.ok) { const d = await res.json(); setDocs(Array.isArray(d) ? d : d?.results || []); }
    } catch (e) {}
    setLoading(false);
  };

  // Follow DRF pagination (`next` links) to load every row, not just page 1.
  const fetchAllPages = async (startUrl) => {
    let out = [];
    let url = startUrl;
    let guard = 0;
    while (url && guard < 100) {
      guard++;
      let d;
      try {
        const r = await fetch(url, { credentials: 'same-origin' });
        if (!r.ok) break;
        d = await r.json();
      } catch (e) { break; }
      if (Array.isArray(d)) { out = out.concat(d); break; }
      out = out.concat(d.results || d.all_programs || []);
      if (d.next) { try { const u = new URL(d.next); url = u.pathname + u.search; } catch { url = d.next; } }
      else url = null;
    }
    return out;
  };

  const loadMeta = async () => {
    try {
      const fRes = await fetch('/api/documents/folders/', { credentials: 'same-origin' });
      if (fRes.ok) setFolders(await fRes.json());
    } catch (e) {}
    try { setPrograms(await fetchAllPages('/api/development/programs/')); } catch (e) {}
    try { setCustomers(await fetchAllPages('/api/customers/customers/')); } catch (e) {}
    try {
      const meRes = await fetch('/api/auth/session-login/', { credentials: 'same-origin' });
      if (meRes.ok) setCurrentUser(await meRes.json());
    } catch (e) {}
  };

  useEffect(() => { loadMeta(); loadDocs('all', ''); }, []);

  // Debounced server-side search; also refires when the active folder changes.
  useEffect(() => {
    const h = setTimeout(() => { setLoading(true); loadDocs(activeFolder, search); }, 250);
    return () => clearTimeout(h);
  }, [search, activeFolder]);

  const handleUpload = async () => {
    if (!form.title || !file || !form.folder) return alert('Folder, title, and file are required');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', form.folder);
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('tags', form.tags);
      if (form.program) fd.append('program', form.program);
      if (form.customer) fd.append('customer', form.customer);
      const res = await fetch('/api/documents/', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'X-CSRFToken': csrf() }, body: fd,
      });
      if (res.ok) {
        setShowUpload(false);
        setForm({ title: '', description: '', tags: '', program: '', customer: '', folder: '' });
        setFile(null);
        setLoading(true); loadDocs(activeFolder, search);
      } else { alert('Error: ' + await res.text()); }
    } catch (e) { alert('Upload failed'); }
    setSaving(false);
  };

  const canDelete = (d) => {
    if (!currentUser) return false;
    const isManager = currentUser.role === 'engineering_manager' || currentUser.role === 'executive';
    return isManager || d.uploaded_by === currentUser.id;
  };

  const handleDelete = async (d) => {
    if (!window.confirm('Delete "' + d.title + '"? This cannot be undone.')) return;
    try {
      const res = await fetch('/api/documents/' + d.id + '/', {
        method: 'DELETE', credentials: 'same-origin', headers: { 'X-CSRFToken': csrf() },
      });
      if (res.ok || res.status === 204) { setLoading(true); loadDocs(activeFolder, search); }
      else alert('Could not delete (you may not have permission).');
    } catch (e) {}
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const tabs = [{ value: 'all', label: 'All Docs' }, ...folders];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ color: theme.text, fontSize: 20, fontWeight: 700, margin: 0 }}>Documents</h2>
        <button onClick={() => setShowUpload(!showUpload)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer' }}>+ Upload Document</button>
      </div>

      {showUpload && (
        <div style={{ background: theme.surface, border: `1px solid ${theme.accent}44`, borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ color: theme.text, fontSize: 16, fontWeight: 600 }}>Upload Document</span>
            <button onClick={() => setShowUpload(false)} style={{ background: 'none', border: 'none', color: theme.textDim, cursor: 'pointer', fontSize: 18 }}>×</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
            <div><label style={lStyle}>Folder *</label><select value={form.folder} onChange={e => setForm(p => ({ ...p, folder: e.target.value }))} style={{ ...iStyle, appearance: 'auto' }}>
              <option value="">— Select folder —</option>
              {folders.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select></div>
            <div><label style={lStyle}>Title *</label><input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Document title" style={iStyle} /></div>
            <div><label style={lStyle}>Program (optional)</label><select value={form.program} onChange={e => setForm(p => ({ ...p, program: e.target.value }))} style={{ ...iStyle, appearance: 'auto' }}>
              <option value="">— None —</option>
              {programs.map(p => <option key={p.id} value={p.id}>{p.code}{p.title ? ' — ' + p.title : ''}</option>)}
            </select></div>
            <div><label style={lStyle}>Customer (optional)</label><select value={form.customer} onChange={e => setForm(p => ({ ...p, customer: e.target.value }))} style={{ ...iStyle, appearance: 'auto' }}>
              <option value="">— None —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          </div>
          <div style={{ marginTop: 10 }}><label style={lStyle}>Tags (optional, comma-separated keywords)</label><input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="e.g. Sprint 14, GDL, cross-section" style={iStyle} /></div>
          <div style={{ marginTop: 10 }}><label style={lStyle}>Description (optional)</label><textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description..." style={{ ...iStyle, height: 50, resize: 'vertical' }} /></div>
          <div style={{ marginTop: 10 }}>
            <label style={lStyle}>File *</label>
            <div onClick={() => fileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
              style={{ border: `2px dashed ${file ? theme.green : theme.border}`, borderRadius: 8, padding: file ? 10 : 20, textAlign: 'center', cursor: 'pointer' }}>
              <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
              {file ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: theme.text, fontSize: 13, flex: 1 }}>{file.name}</span>
                  <span style={{ color: theme.textDim, fontSize: 11 }}>{formatSize(file.size)}</span>
                  <button onClick={e => { e.stopPropagation(); setFile(null); }} style={{ background: 'none', border: 'none', color: theme.red, cursor: 'pointer' }}>×</button>
                </div>
              ) : <div style={{ color: theme.textDim, fontSize: 12 }}>Drop file here or click to browse</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <button onClick={() => setShowUpload(false)} style={{ padding: '8px 16px', borderRadius: 6, background: 'transparent', color: theme.textMuted, border: `1px solid ${theme.border}`, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={handleUpload} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{saving ? 'Uploading...' : 'Upload'}</button>
          </div>
        </div>
      )}

      {/* Folder tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.value} onClick={() => setActiveFolder(t.value)}
            style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: activeFolder === t.value ? `1px solid ${theme.accent}` : `1px solid ${theme.border}`,
              background: activeFolder === t.value ? theme.accentGlow : 'transparent',
              color: activeFolder === t.value ? theme.accent : theme.textMuted, whiteSpace: 'nowrap' }}>
            {t.value !== 'all' && <span style={{ marginRight: 5 }}>{folderIcon[t.value] || '📁'}</span>}{t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title, description, tags, uploader, program, customer..."
          style={{ width: '100%', padding: '10px 14px 10px 36px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textDim} strokeWidth="2" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
      </div>

      {loading ? <div style={{ color: theme.textDim, padding: 40, textAlign: 'center' }}>Loading...</div> : (
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {docs.length === 0 && <div style={{ color: theme.textDim, textAlign: 'center', padding: 40 }}>{search ? 'No documents match your search.' : 'No documents here yet. Click "+ Upload Document" to add one.'}</div>}
          {docs.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 8 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
              <span style={{ fontSize: 20 }}>{folderIcon[d.folder] || '📄'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: theme.text, fontSize: 13, fontWeight: 500 }}>{d.title}</div>
                <div style={{ color: theme.textDim, fontSize: 11, marginTop: 2 }}>
                  {d.folder_label}
                  {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''}
                  {` · ${timeAgo(d.uploaded_at)}`}
                  {d.program_name ? ` · ${d.program_name}` : ''}
                  {d.customer_name ? ` · ${d.customer_name}` : ''}
                  {d.file_size ? ` · ${formatSize(d.file_size)}` : ''}
                </div>
                {d.tags && <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>{d.tags.split(',').filter(Boolean).map((t, i) => <span key={i} style={{ background: theme.accent + '18', color: theme.accent, fontSize: 9, padding: '1px 5px', borderRadius: 3 }}>{t.trim()}</span>)}</div>}
              </div>
              <a href={'/api/documents/' + d.id + '/view/'} target="_blank" rel="noreferrer" style={{ color: theme.accent, fontSize: 11, textDecoration: 'none', whiteSpace: 'nowrap' }}>View</a>
              <a href={'/api/documents/' + d.id + '/download/'} download style={{ color: theme.accent, fontSize: 11, textDecoration: 'none', whiteSpace: 'nowrap' }}>↓ Download</a>
              {canDelete(d) && <button onClick={() => handleDelete(d)} title="Delete" style={{ background: 'none', border: 'none', color: theme.textDim, cursor: 'pointer', fontSize: 15, padding: '0 2px' }}>×</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
